import { NavLink as RouterNavLink, NavLinkProps, useLocation } from "react-router-dom";
import { forwardRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/hooks/useLanguage";

interface NavLinkCompatProps extends Omit<NavLinkProps, "className"> {
  className?: string | ((props: { isActive: boolean; isPending: boolean }) => string);
  activeClassName?: string;
  pendingClassName?: string;
}

const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName, to, ...props }, ref) => {
    const location = useLocation();
    const { language } = useLanguage();

    // Add language prefix to the path if it doesn't already have one
    const pathWithLang = useMemo(() => {
      if (!to || typeof to !== "string") return to;

      // If path already starts with a language prefix, return as is
      if (to.match(/^\/(cs|en)(\/|$)/)) {
        return to;
      }

      // If path is root, return language prefix
      if (to === "/") {
        return `/${language}`;
      }

      // Otherwise, prepend language prefix
      return `/${language}${to.startsWith("/") ? to : `/${to}`}`;
    }, [to, language]);

    return (
      <RouterNavLink
        ref={ref}
        to={pathWithLang}
        className={({ isActive, isPending }) => {
          if (typeof className === "function") {
            return cn(
              className({ isActive, isPending }),
              isActive && activeClassName,
              isPending && pendingClassName
            );
          }
          return cn(className, isActive && activeClassName, isPending && pendingClassName);
        }}
        {...props}
      />
    );
  }
);

NavLink.displayName = "NavLink";

export { NavLink };
